/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as React from 'react';
import { Map as FullwidthMap, IMapGeoObject, IRouteData } from '@cian/frontend-newbuilding-map-component';
import { ArticleHeading1 } from '@cian/ui-kit';
import classNames from 'classnames';

import { ICoordinates } from '../../types/geo';
import { Layout } from '../Layout';
import * as styles from './Map.css';
import { IRoutingBuildSuccessParams } from './types';

export interface IMapProps {
  apiKey: string;
  apiVersion?: string;
  currentTypes: string[];
  infrastructureTypes?: React.ReactNode;
  isFullscreen?: boolean;
  mapRef?: React.Ref<FullwidthMap>;
  nearbyNewbuildingsLink?: React.ReactNode;
  onActiveRouteChange?(route: IRouteData | null): void;
  onBalloonHtmlFirstFetch?: (item: IMapGeoObject) => Promise<string>;
  onBalloonOpen?: (item: IMapGeoObject) => void;
  onError?: (error: Error) => void;
  onGeoObjectsFirstFetch: () => Promise<IMapGeoObject[]>;
  onFullscreenEnter?(): void;
  onFullscreenExit?(): void;
  onMapLoaded?(): void;
  onNoPanoramaEnter?(): void;
  onPanoramaExit?(): void;
  onPanoramaSuccessEnter?(): void;
  onRouteBuildingInit?(): void;
  onRouteBuildingSuccess?(params: IRoutingBuildSuccessParams): void;
  onRoutingStart?(): void;
  onRoutingEnd?(): void;
  onRulerClick?(): void;
  pinCoordinates: ICoordinates;
  routeButton?: React.ReactNode;
  specialPromo?: React.ReactNode;
  zoom: number;
}

function Component({
  apiKey,
  apiVersion,
  currentTypes,
  infrastructureTypes,
  isFullscreen,
  mapRef,
  nearbyNewbuildingsLink,
  onActiveRouteChange,
  onBalloonHtmlFirstFetch,
  onBalloonOpen,
  onFullscreenEnter,
  onFullscreenExit,
  onGeoObjectsFirstFetch,
  onError,
  onMapLoaded,
  onNoPanoramaEnter,
  onPanoramaExit,
  onPanoramaSuccessEnter,
  onRouteBuildingInit,
  onRouteBuildingSuccess,
  onRoutingStart,
  onRoutingEnd,
  onRulerClick,
  pinCoordinates,
  routeButton,
  specialPromo,
  zoom,
}: IMapProps) {
  return (
    <div className={styles['wrapper']} data-nav-tab="infrastructure/Инфраструктура" id="infrastructure">
      <Layout>
        <div className={styles['header']}>
          <ArticleHeading1 as={'h2'} fontWeight={'bold'}>
            Инфраструктура
          </ArticleHeading1>
          <div className={styles['links']}>
            {specialPromo}
            {nearbyNewbuildingsLink}
          </div>
        </div>
      </Layout>
      <div className={classNames(styles['container'], isFullscreen && styles['fullscreen-mode'])}>
        <FullwidthMap
          ref={mapRef}
          geoObjectNames={currentTypes}
          media={[
            {
              minWidth: -Infinity,
              maxWidth: Infinity,
              options: {
                center: pinCoordinates,
                styles: { width: '100%', height: '100%' },
                animationDuration: 300,
                defaultZoom: zoom,
              },
            },
          ]}
          firstPaintOptions={{
            disableScrollZoom: true,
            displayRouteInfo: true,
            fetch: {
              onGeoObjectsFetch: onGeoObjectsFirstFetch,
              onBalloonHtmlFetch: onBalloonHtmlFirstFetch,
            },
            id: 'infrastructure-map',
            styles: { width: '100%', height: '100%' },
            controls: [
              { type: 'ZoomControl' },
              { type: 'RulerControl', onClick: onRulerClick },
              { type: 'GeolocationControl' },
              { type: 'FullscreenControl' },
              { type: 'PanoramaControl' },
            ],
            zoomRange: { min: 1, max: 17 },
            apiKey,
            apiVersion,
            onActiveRouteChange,
            onBalloonOpen,
            onError,
            onFullscreenEnter,
            onFullscreenExit,
            onMapSuccess: onMapLoaded,
            onNoPanoramaEnter,
            onPanoramaExit,
            onPanoramaSuccessEnter,
            onRouteBuildingInit,
            onRouteBuildingSuccess,
            onRoutingEnd,
            onRoutingStart,
          }}
        />
        {routeButton && <div className={styles['route-button']}>{routeButton}</div>}
        {infrastructureTypes && <div className={styles['infrastructure']}>{infrastructureTypes}</div>}
      </div>
    </div>
  );
}

Component.displayName = 'Map';

export const Map = Component;
